/* Bruno's Place: Fan favorites tabs, carousel and quick add (feeds Horizon's cart drawer) */
import { CartLinesUpdateEvent } from '@shopify/events';

/* ---------- Quick add, shared by every Bruno's product card on the page ---------- */
async function bpAddToCart(trigger, variantId, quantity = 1, formData = null) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(() => {});

  trigger.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: Number(quantity) || 1 }],
      promise,
    })
  );

  try {
    // Use the product form when given, so app fields (uploads, preorder notes) travel with the item
    const body = formData || new FormData();
    if (!formData) {
      body.append('id', String(variantId));
      body.append('quantity', String(quantity || 1));
    }
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bp-product-card',
        itemCount: Number(quantity) || 1,
        productId: data.product_id,
        sections: data.sections,
        didError: false,
      },
    });
    return true;
  } catch (error) {
    console.error(error);
    reject(error);
    return false;
  }
}

window.bpAddToCart = bpAddToCart;

if (!window.bpCardsBound) {
  window.bpCardsBound = true;

  document.addEventListener('click', async (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    const card = target.closest?.('[data-bp-card]');
    if (!card) return;

    const open = target.closest('[data-bp-open-sizes]');
    if (open) {
      document.querySelectorAll('[data-bp-card].is-picking').forEach((c) => c !== card && closeSizes(c));
      card.classList.add('is-picking');
      open.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => card.querySelector('.bp-card2__size:not(:disabled)')?.focus());
      return;
    }

    if (target.closest('[data-bp-close-sizes]')) {
      closeSizes(card, true);
      return;
    }

    const add = target.closest('[data-bp-add]');
    if (add && !add.disabled && !add.classList.contains('is-busy')) {
      const original = add.innerHTML;
      add.classList.add('is-busy');
      if (add.classList.contains('bp-card2__size')) add.textContent = '…';
      const ok = await bpAddToCart(add, add.dataset.bpAdd);
      add.classList.remove('is-busy');
      if (ok) {
        add.classList.add('is-done');
        if (add.classList.contains('bp-card2__size')) add.textContent = '✓';
        setTimeout(() => {
          add.classList.remove('is-done');
          add.innerHTML = original;
          closeSizes(card);
        }, 1100);
      } else {
        add.innerHTML = original;
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const card = document.querySelector('[data-bp-card].is-picking');
    if (card) closeSizes(card, true);
  });

  // Click outside closes an open size sheet
  document.addEventListener('pointerdown', (e) => {
    const open = document.querySelector('[data-bp-card].is-picking');
    if (open && !open.contains(/** @type {Node} */ (e.target))) closeSizes(open);
  });
}

function closeSizes(card, focusButton = false) {
  card.classList.remove('is-picking');
  const btn = card.querySelector('[data-bp-open-sizes]');
  btn?.setAttribute('aria-expanded', 'false');
  if (focusButton) btn?.focus();
}

/* ---------- Section: tabs + carousel ---------- */
class BpFeatured extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-bp-tab]')];
    this.panels = [...this.querySelectorAll('[data-bp-panel]')];
    this.pill = this.querySelector('.bp-ft__tab-pill');
    this.prev = this.querySelector('[data-bp-prev]');
    this.next = this.querySelector('[data-bp-next]');
    this.bar = this.querySelector('[data-bp-progress]');
    this.active = 0;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(n);
        this.tabs[n].focus();
      });
    });

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    this.onScroll = () => this.update();
    this.panels.forEach((p) => p.querySelector('[data-bp-track]')?.addEventListener('scroll', this.onScroll, { passive: true }));
    this.onResize = () => {
      this.movePill(true);
      this.update();
    };
    window.addEventListener('resize', this.onResize);

    this.onSelect = (e) => {
      const i = this.tabs.indexOf(e.target);
      if (i > -1) this.select(i);
    };
    document.addEventListener('shopify:block:select', this.onSelect);

    requestAnimationFrame(() => {
      this.movePill(true);
      this.update();
    });
    document.fonts?.ready.then(() => this.movePill(true));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onSelect);
  }

  get track() {
    return this.panels[this.active]?.querySelector('[data-bp-track]');
  }

  select(i) {
    if (i === this.active && this.panels[i] && !this.panels[i].hidden) return;
    this.tabs.forEach((t, n) => {
      const on = n === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((p, n) => {
      const on = n === i;
      p.hidden = !on;
      p.classList.toggle('is-active', on);
      if (on) {
        p.classList.remove('is-entering');
        void p.offsetWidth;
        p.classList.add('is-entering');
        const t = p.querySelector('[data-bp-track]');
        if (t) t.scrollLeft = 0;
      }
    });
    this.active = i;
    this.movePill();
    this.update();
  }

  movePill(instant = false) {
    const tab = this.tabs[this.active];
    if (!this.pill || !tab) return;
    if (instant) this.pill.style.transition = 'none';
    this.pill.style.setProperty('--pill-x', `${tab.offsetLeft}px`);
    this.pill.style.setProperty('--pill-w', `${tab.offsetWidth}px`);
    if (instant) requestAnimationFrame(() => this.pill.style.removeProperty('transition'));
    // Keep the active tab in view on phones
    const bar = tab.parentElement;
    if (bar && bar.scrollWidth > bar.clientWidth) {
      bar.scrollTo({ left: tab.offsetLeft - 24, behavior: instant ? 'auto' : 'smooth' });
    }
  }

  page(dir) {
    const t = this.track;
    if (!t) return;
    t.scrollBy({ left: dir * t.clientWidth * 0.85, behavior: 'smooth' });
  }

  update() {
    const t = this.track;
    if (!t) return;
    const max = t.scrollWidth - t.clientWidth;
    const ratio = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
    const pos = max > 0 ? t.scrollLeft / max : 0;
    if (this.bar) {
      this.bar.style.setProperty('--w', `${Math.max(ratio * 100, 8)}%`);
      this.bar.style.setProperty('--x', `${pos * (1 / Math.max(ratio, 0.08) - 1) * 100}%`);
    }
    if (this.prev) this.prev.disabled = t.scrollLeft <= 4;
    if (this.next) this.next.disabled = t.scrollLeft >= max - 4;
  }
}

if (!customElements.get('bp-featured')) customElements.define('bp-featured', BpFeatured);
